/**
 * Load connector declarations from skaile.yaml.
 *
 * The top-level `mounts:` key is no longer accepted (hard cut as of Task 11).
 * Filesystem-projected backends must be declared under `connectors:` with a
 * `mount:` sub-block. See `docs/migration-mounts-to-connectors.md`.
 */

import type { ConnectorDeclaration as WorkspaceConnector } from "@skaile/workspaces/core";
import { resolveSkWorkspaceConfig } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { ResolvedSkaileConfig } from "@skaile/workspaces/types";
import type { ConnectorDeclaration } from "./connector-types.js";
import { loadMaterializedConnectorDeclarations } from "./materialized-connectors.js";

const configLog = createLogger({ kind: "connector", subkind: "config" });

/**
 * Read `skaile.yaml` in `projectDir` and return all declared connectors as `ConnectorDeclaration[]`.
 *
 * Throws a migration error when the legacy top-level `mounts:` key is present.
 *
 * @param projectDir - Root of the workspace (directory containing `skaile.yaml`).
 * @returns Merged array of connector declarations from `skaile.yaml` and the materialized
 *   `.skaile/assets/connector/*` disk projection (Phase 2.5); empty array if neither source
 *   contributes any entries. `skaile.yaml` wins on id collision.
 * @docLink packages/connectors/api-reference#load-connector-declarations
 */
export function loadConnectorDeclarations(projectDir: string): ConnectorDeclaration[] {
  const config = resolveSkWorkspaceConfig(projectDir);

  // Hard cut: reject legacy top-level `mounts:` key with an actionable message.
  if (config.mounts && config.mounts.length > 0) {
    throw new Error(
      "'mounts:' is no longer supported — move entries under 'connectors:' with a 'mount:' sub-block. See docs/migration-mounts-to-connectors.md",
    );
  }

  const yamlDecls =
    config.connectors && config.connectors.length > 0
      ? config.connectors.map(workspaceConnectorToDeclaration)
      : [];

  // Library-assigned connectors reach the runner via the materialized disk
  // projection (Phase 2.5). skaile.yaml is explicit user config — it wins on id
  // collision; the shadowed materialized connector is logged, not silently dropped.
  const byId = new Map<string, ConnectorDeclaration>();
  for (const d of yamlDecls) byId.set(d.id, d);
  for (const d of loadMaterializedConnectorDeclarations(projectDir)) {
    if (byId.has(d.id)) {
      configLog.warn(
        `materialized connector '${d.id}' shadowed by an explicit skaile.yaml connector`,
      );
      continue;
    }
    byId.set(d.id, d);
  }
  return [...byId.values()];
}

function workspaceConnectorToDeclaration(c: WorkspaceConnector): ConnectorDeclaration {
  const decl: ConnectorDeclaration = {
    id: c.id,
    driver: c.driver,
    access: c.access ?? "read-only",
    auth: c.auth,
    options: c.options,
  };

  // Backend-auth routing key for live token refresh; dropping it breaks git mount mint.
  if (c.providerLinkId) decl.providerLinkId = c.providerLinkId;

  // Map the optional mount sub-block when present.
  if (c.mount) {
    decl.mount = {
      source: c.mount.source,
      target: c.mount.target,
      watch: c.mount.watch,
      exposeAccessToken: c.mount.exposeAccessToken,
      accessTokenTTL: c.mount.accessTokenTTL,
    };
  }

  return decl;
}

/**
 * Map a Protocol v3 `session_init` `resolvedConfig` to `ConnectorDeclaration[]`.
 *
 * Wire mounts are flat (`source` / `target` / `watch` top-level per
 * `ResolvedSkaileConfig`); declarations carry them in the `mount:` sub-block.
 * Wire `connectors` (tool-face) map without a mount block. Malformed entries
 * (missing `id`, `driver`, or — for mounts — `source`) are logged and dropped,
 * never thrown: one bad wire entry must not kill session boot. Duplicate ids
 * (within or across the two arrays) keep the FIRST occurrence — mounts precede
 * connectors — and the shadowed entry is logged and dropped the same way.
 *
 * Pure function, no I/O. Used by the runner as the disk-first fallback when
 * `skaile.yaml` declares no connectors (e.g. broker mode, where only the
 * workspace subdirectory is bound and no yaml file exists).
 *
 * @param resolved - `mounts` / `connectors` arrays from the platform-resolved config.
 * @returns Declarations in wire order — mounts first, then connectors.
 * @docLink packages/connectors/api-reference#resolved-config-to-declarations
 */
export function resolvedConfigToDeclarations(
  resolved: Pick<ResolvedSkaileConfig, "mounts" | "connectors">,
): ConnectorDeclaration[] {
  // Keyed by id: the wire carries no well-formedness guarantee, and a duplicate
  // id downstream would collide on mount paths / tool namespaces. First wins.
  const byId = new Map<string, ConnectorDeclaration>();
  const pushUnlessDuplicate = (kind: "mount" | "connector", decl: ConnectorDeclaration): void => {
    if (byId.has(decl.id)) {
      configLog.warn(`dropping duplicate resolvedConfig ${kind} (id already declared)`, {
        id: decl.id,
      });
      return;
    }
    byId.set(decl.id, decl);
  };

  for (const m of resolved.mounts ?? []) {
    // A mount without a source cannot connect — drop it with the malformed ones.
    if (
      typeof m?.id !== "string" ||
      typeof m?.driver !== "string" ||
      typeof m.source !== "string"
    ) {
      configLog.warn("dropping malformed resolvedConfig mount (missing id/driver/source)", {
        id: (m as { id?: unknown } | undefined)?.id,
      });
      continue;
    }
    const decl: ConnectorDeclaration = {
      id: m.id,
      driver: m.driver,
      access: m.access ?? "read-only",
      auth: m.auth,
      options: m.options,
      mount: {
        source: m.source,
        target: m.target,
        watch: m.watch,
        exposeAccessToken: m.exposeAccessToken,
        accessTokenTTL: m.accessTokenTTL,
      },
    };
    // Backend-auth routing key for live token refresh; dropping it breaks git mount mint.
    if (m.providerLinkId) decl.providerLinkId = m.providerLinkId;
    pushUnlessDuplicate("mount", decl);
  }

  for (const c of resolved.connectors ?? []) {
    if (typeof c?.id !== "string" || typeof c?.driver !== "string") {
      configLog.warn("dropping malformed resolvedConfig connector (missing id/driver)", {
        id: (c as { id?: unknown } | undefined)?.id,
      });
      continue;
    }
    const decl: ConnectorDeclaration = {
      id: c.id,
      driver: c.driver,
      access: c.access ?? "read-only",
      auth: c.auth,
      options: c.options,
    };
    if (c.providerLinkId) decl.providerLinkId = c.providerLinkId;
    pushUnlessDuplicate("connector", decl);
  }

  return [...byId.values()];
}

import type { ForgeSecretProvider, SecretProvider } from "./secrets.js";
import { EnvSecretProvider, InMemorySecretProvider, SecretProviderChain } from "./secrets.js";

/**
 * Build a `SecretProviderChain` for Forge session context.
 * `OAuthSecretProvider` is not wired here — it must be provided externally
 * (the Forge session manager constructs it and passes it in).
 * @param forgeProvider - The Forge-provisioned secret store.
 * @param extraProviders - Additional providers prepended to the chain (e.g. an OAuth provider).
 * @returns A chain that routes `forge:` refs to the Forge store and `env:` refs to process.env.
 * @docLink packages/connectors/api-reference#create-forge-secret-provider-chain
 */
export function createForgeSecretProviderChain(
  forgeProvider: ForgeSecretProvider,
  extraProviders: SecretProvider[] = [],
): SecretProviderChain {
  return new SecretProviderChain([...extraProviders, forgeProvider, new EnvSecretProvider()]);
}

/**
 * Build a `SecretProviderChain` for CLI/standalone context.
 * `EnvSecretProvider` takes priority; `extraSecrets` are loaded into an `InMemorySecretProvider`.
 * @param extraSecrets - Inline secrets injected into memory (e.g. tokens passed via CLI flags).
 * @returns A chain that resolves `env:` refs from process.env plus any inline extras.
 * @docLink packages/connectors/api-reference#create-cli-secret-provider-chain
 */
export function createCliSecretProviderChain(
  extraSecrets: Record<string, string> = {},
): SecretProviderChain {
  const mem = new InMemorySecretProvider();
  if (Object.keys(extraSecrets).length > 0) {
    mem.provision(extraSecrets);
  }
  return new SecretProviderChain([new EnvSecretProvider(), mem]);
}

// ── Auth grammar (mount-side, Tier 2 credential tiers) ───────────────────────

/**
 * Provider kind for a mount auth ref.
 *
 * Two values are accepted:
 *   - `pat`     — static personal access token resolved via the secrets chain
 *                 (standalone CLI / non-platform contexts).
 *   - `backend` — token minted by the platform's credential mediator over the
 *                 transport (`request_access_token` round-trip). Provider
 *                 dispatch (OAuth, GitHub App, ...) lives backend-side; the
 *                 runner stays provider-agnostic.
 *
 * @docLink packages/connectors/api-reference#mount-auth-kind
 */
export type MountAuthKind = "pat" | "backend";

/**
 * Parsed mount `auth:` field.
 *
 *   - `pat`     → `value` is the inner secret ref (e.g. `env:NAME`).
 *   - `backend` → `value` is empty (sentinel only); the runner asks the
 *                 backend for tokens via `request_access_token`.
 *
 * @docLink packages/connectors/api-reference#mount-auth-ref
 */
export interface MountAuthRef {
  kind: MountAuthKind;
  /** Prefix-stripped payload — see kind for shape. Empty for `backend`. */
  value: string;
  /** Original auth string from the declaration, kept for diagnostics. */
  raw: string;
}

/**
 * Thrown when a mount declaration's `auth:` field uses the legacy bare `env:NAME` grammar
 * or any other unrecognized form. Migrate to `pat:env:NAME` (standalone) or `backend`
 * (platform-mediated).
 * @docLink packages/connectors/api-reference#legacy-auth-grammar-error
 */
export class LegacyAuthGrammarError extends Error {
  readonly auth: string;
  constructor(auth: string, mountId?: string) {
    const where = mountId ? ` on mount '${mountId}'` : "";
    super(
      `Legacy or unrecognized auth grammar${where} ('${auth}'). ` +
        `Replace with one of:\n` +
        `  - backend                  (platform-mediated, recommended)\n` +
        `  - pat:env:NAME             (standalone CLI / static token)\n` +
        `See _devlog/specs/2026-05-06-tier-2-credential-mediation.md.`,
    );
    this.name = "LegacyAuthGrammarError";
    this.auth = auth;
  }
}

/**
 * Resolve the `auth: backend` initial mint for a given mount or connector id
 * by querying the secrets chain with a `${kind}:${id}` ref. Returns the
 * resolved bearer token or `undefined` if no pre-minted credential is
 * available.
 *
 * Used by the runner when wiring `ConnectorManager` to
 * answer initial-mint requests from `PreMintedSecretProvider` without
 * round-tripping to the platform. Drivers continue to receive a
 * `tokenMediator` for refresh + retry-401 paths.
 *
 * @param kind Resource family (`mount` or `connector`).
 * @param id Resource declaration id.
 * @param chain Secrets chain. Pass `undefined` to return `undefined`.
 * @returns The pre-minted token, or `undefined` if not stored on the chain.
 * @docLink packages/connectors/api-reference#resolve-backend-auth-for
 */
export function resolveBackendAuthFor(
  kind: "mount" | "connector",
  id: string,
  chain: import("./secrets.js").SecretProvider | undefined,
): string | undefined {
  if (!chain) return undefined;
  return chain.resolve(`${kind}:${id}`);
}

/**
 * Parse a mount-side auth ref into a typed `MountAuthRef`.
 *
 * Accepted forms:
 *   - `backend`      — platform-mediated mint via `request_access_token`.
 *   - `pat:env:NAME` — static PAT resolved from the secrets chain.
 *
 * Rejects bare `env:NAME` (legacy) and provider-prefixed grammars (`oauth:`,
 * `github-app:`) — those moved backend-side as part of Tier-2 mediation.
 * Returns `undefined` when `auth` is absent or empty.
 *
 * @param auth - Raw `auth:` value from the mount declaration.
 * @param mountId - Optional mount id included in error messages.
 * @returns Parsed `MountAuthRef` or `undefined`.
 * @throws {LegacyAuthGrammarError} for any unrecognized grammar.
 * @docLink packages/connectors/api-reference#resolve-auth-ref
 */
export function resolveAuthRef(
  auth: string | undefined,
  mountId?: string,
): MountAuthRef | undefined {
  if (!auth) return undefined;

  if (auth === "backend") {
    return { kind: "backend", value: "", raw: auth };
  }
  if (auth.startsWith("pat:")) {
    return { kind: "pat", value: auth.slice("pat:".length), raw: auth };
  }

  // Legacy bare `env:NAME` and the deprecated provider-prefixed grammars
  // (`oauth:`, `github-app:`) all land here.
  throw new LegacyAuthGrammarError(auth, mountId);
}
